import React from 'react';
import { Box, Grid, Skeleton, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import {
  dimGrey,
  lightBackground1,
  lightBlack,
  lightGrey,
  outlineSeparator,
} from '../../../../../mainTheme';
import type { StatCard } from './surveyStatCards';

interface StatCardsGridProps {
  cards: StatCard[];
  testId?: string;
}

// Row of summary tiles (label, value, sub line, info tooltip). Same tile
// styling as the medication stats cards on the Medication tab. The tile
// contents are built by the caller (buildStatCards for a patient,
// buildFacilityStatCards for a location).
const StatCardsGrid: React.FC<StatCardsGridProps> = ({
  cards,
  testId = 'survey-stats-cards',
}) => (
  <Grid container spacing={2.5} data-testid={testId}>
    {cards.map((card) => (
      <Grid item xs={6} sm={4} lg={2} key={card.key}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            p: 1.5,
            height: '100%',
            borderRadius: '8px',
            backgroundColor: lightBackground1,
            border: `1px solid ${outlineSeparator}`,
          }}
        >
          <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
            <Typography
              sx={{
                color: dimGrey,
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px',
              }}
            >
              {card.label}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {card.icon && (
                <FontAwesomeIcon
                  icon={card.icon.icon}
                  color={card.icon.color}
                  fontSize={18}
                  data-testid={card.icon.testId}
                />
              )}
              {card.pending ? (
                <Skeleton
                  width={64}
                  height={28}
                  data-testid={`stat-pending-${card.key}`}
                />
              ) : (
                <Typography
                  sx={{
                    color: lightBlack,
                    fontSize: '20px',
                    fontWeight: 700,
                    lineHeight: '28px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {card.value}
                </Typography>
              )}
            </Box>
            {card.sub && (
              <Typography
                sx={{
                  color: dimGrey,
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '16px',
                }}
              >
                {card.sub}
              </Typography>
            )}
          </Box>
          <Tooltip
            title={card.description}
            placement="bottom-end"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: lightBackground1,
                  color: dimGrey,
                  border: `1px solid ${outlineSeparator}`,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  p: 2,
                  maxWidth: '18.75rem',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '16px',
                },
              },
              arrow: { sx: { color: lightBackground1 } },
            }}
          >
            <Box
              component="span"
              aria-label={`${card.label} info`}
              sx={{ display: 'flex', cursor: 'pointer' }}
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                color={lightGrey}
                fontSize={12}
              />
            </Box>
          </Tooltip>
        </Box>
      </Grid>
    ))}
  </Grid>
);

export default StatCardsGrid;
